<template>
    <BaseSection
        v-if="siteId"
        class="container-narrow"
    >
        <div class="feedback">
            <p
                v-if="status && status === 'success'"
                class="feedback__question"
            >
                {{ t('feedback-thanks') }}
            </p>
            <p
                v-else
                class="feedback__question"
            >
                {{ t('feedback-question') }}
            </p>
            <div
                v-if="state === undefined"
                class="feedback__buttons"
            >
                <BaseButton
                    class="button--secondary"
                    @click="handleFeedbackYes"
                >
                    {{ t('feedback-yes') }}
                </BaseButton>
                <BaseButton
                    class="button--secondary"
                    @click="handleFeedbackNo"
                >
                    {{ t('feedback-no') }}
                </BaseButton>
            </div>
            <div
                v-else-if="state === 0 && (!status || !['pending', 'success'].includes(status))"
                class="feedback__form"
            >
                <div class="feedback__inputs">
                    <BaseInput
                        :value="reason"
                        :placeholder="t('feedback-reason')"
                        @clear="reason = ''"
                        @update:model-value="reason = $event.target.value"
                    />
                    <BaseButton
                        class="button--secondary"
                        @click="sendFeedback"
                    >
                        {{ t('feedback-send') }}
                    </BaseButton>
                </div>
            </div>
        </div>
    </BaseSection>
</template>

<script setup lang="ts">
import FeedbackMutation from '~/graphql/mutations/Feedback.graphql';

type Props = {
    id: number | string;
}

const t = useTranslation('components');

// Get id via: query users { users(email: "anonymous@digitalnatives.nl"){ email id } }
const { public: { anonymousId }} = useRuntimeConfig() ?? {};
const { activeSite: site, siteIds } = useMultisite();
const handle = toValue(site)?.handle;
const siteId = siteIds.find((siteConfig) => siteConfig.handle === handle)?.id;

const props = defineProps<Props>();

const status = ref(undefined);

const state = ref(undefined);
const reason = ref('');

async function handleFeedbackYes() {
    state.value = 1;
    status.value = 'pending';
    await sendFeedback();
}

function handleFeedbackNo() {
    state.value = 0;
}

const { trackEvent } = useCustomGtmEvent();
const { execute } = useAsyncMutation('feedback');

async function sendFeedback() {
    status.value = 'pending';

    const title = `[${props.id}] - ${new Date().toISOString()} - ${state.value === 1 ? 'Yes' : 'No'}`;
    await execute({
        variables: {
            siteId: siteId,
            feedbackReason: reason.value,
            feedbackValue: state.value,
            feedbackEntry: [parseInt(props.id, 10)],
            title: title,
            authorId: anonymousId,
        },
        mutation: FeedbackMutation,
        fetchPolicy: 'no-cache',
    }).then(() => {
        if (state.value === 1) {
            trackEvent({
                event: 'information_feedback',
                feedback: 'positive'
            });
        } else if (state.value === 0) {
            trackEvent({
                event: 'information_feedback',
                feedback: 'negative'
            });
        }

        status.value = 'success';
    }).catch((err) => {
        status.value = 'error';
        console.error(err);
    });
    status.value = 'success';
}
</script>

<style lang="less" src="./BaseFeedback.less"></style>
